import { ErrorLike } from "@apollo/client"
import { useMutation } from "@apollo/client/react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect, useRef, useState } from "react"
import { Control, FieldErrors, useForm } from "react-hook-form"
import { z } from "zod"
import { useFormErrorHandling } from "~/common/error-handling"
import { trackFormError, trackFormStart, trackLead, trackNewsletterSignup } from "~/lib/analytics"
import {
  CreateContactSubmissionDocument,
  CreateFounderSubmissionDocument,
  CreateNewsletterSubscriptionDocument,
} from "~/marketing/__generated__/graphql"
import { Button } from "~/ui/button"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "~/ui/form"
import { TextField } from "~/ui/forms/fields/text-field"
import { FormGeneralErrors } from "~/ui/forms/form-general-errors"
import { GraphqlError } from "~/ui/graphql-error"
import { Textarea } from "~/ui/textarea"
import { ScheduleLink } from "./schedule-link"

const contactValuesSchema = z.object({
  name: z.string().trim().max(255),
  firstName: z.string().trim().max(255),
  lastName: z.string().trim().max(255),
  email: z.string().trim().email("Enter a valid email address."),
  company: z.string().trim().max(255),
  message: z.string().trim().min(1, "Tell us a little about what you need."),
})

const contactSchema = contactValuesSchema.refine(({ name }) => name.length > 0, {
  message: "Enter your name.",
  path: ["name"],
})

const sourceContactSchema = contactValuesSchema
  .refine(({ firstName }) => firstName.length > 0, {
    message: "Enter your first name.",
    path: ["firstName"],
  })
  .refine(({ lastName }) => lastName.length > 0, {
    message: "Enter your last name.",
    path: ["lastName"],
  })

const newsletterSchema = z.object({
  email: z.string().trim().email("Enter a valid email address."),
})

const founderSchema = z.object({
  firstName: z.string().trim().min(1, "Enter your first name.").max(127),
  lastName: z.string().trim().min(1, "Enter your last name.").max(127),
  email: z.string().trim().email("Enter a valid email address."),
})

const SubmissionPanel = ({
  form,
  onFocusCapture,
  status,
  courseEnabled,
}: {
  form: "contact" | "founder" | "newsletter"
  onFocusCapture: () => void
  status: "idle" | "success"
  courseEnabled?: boolean
}) => {
  const headingRef = useRef<HTMLHeadingElement>(null)

  useEffect(() => {
    if (status === "success") headingRef.current?.focus()
  }, [status])

  if (status !== "success") return null

  const newsletter = form === "newsletter"
  const founder = form === "founder"

  return (
    <section
      aria-live="polite"
      className="rounded-2xl border border-border bg-card p-6 text-card-foreground sm:p-8"
      onFocusCapture={onFocusCapture}
    >
      <h2 ref={headingRef} tabIndex={-1} className="home-sans-heading text-2xl font-semibold">
        {newsletter || founder ? "You’re subscribed" : "Thanks — we received your submission"}
      </h2>
      <p className="mt-3 leading-relaxed">
        {newsletter
          ? "Check your inbox to confirm your subscription."
          : founder && courseEnabled
            ? "Your welcome email is on its way. The seven lessons will follow."
            : founder
              ? "You’re on the list. We’ll email you when the series is ready to send."
              : "We’ll be in touch soon. If you’d like, you can book time with us now."}
      </p>
      {!newsletter && (
        <ScheduleLink
          ctaLocation={founder ? "founder-form" : "contact-form"}
          className="mt-6 inline-flex items-center rounded-full bg-primary px-6 py-3 font-semibold text-primary-foreground hover:bg-primary/90"
        >
          Book 30 minutes now
        </ScheduleLink>
      )}
    </section>
  )
}

const mutationError = (error: unknown): ErrorLike =>
  error instanceof Error ? error : new Error("We couldn't send your submission. Please try again.")

const useFormAnalytics = (formName: "contact" | "founder" | "newsletter") => {
  const started = useRef(false)

  return {
    onFocusCapture: () => {
      if (!started.current) {
        trackFormStart(formName)
        started.current = true
      }
    },
    onInvalid: (errors: FieldErrors) => {
      const fields = Object.keys(errors)
      if (fields[0]) trackFormError(formName, fields[0], fields.length)
    },
  }
}

const MessageField = ({
  control,
  disabled,
  label,
  name,
  rows = 5,
}: {
  control: Control<any>
  disabled: boolean
  label: string
  name: "message"
  rows?: number
}) => (
  <FormField
    control={control}
    name={name}
    render={({ field }) => (
      <FormItem>
        <FormLabel>
          {label}{" "}
          <span aria-hidden="true" className="text-destructive">
            *
          </span>
        </FormLabel>
        <FormControl>
          <Textarea {...field} disabled={disabled} rows={rows} aria-required="true" />
        </FormControl>
        <FormMessage className="text-xs" />
      </FormItem>
    )}
  />
)

export const ContactForm = ({
  sourceLayout = false,
  submitLabel,
}: {
  sourceLayout?: boolean
  submitLabel?: string
}) => {
  const form = useForm<z.infer<typeof contactValuesSchema>>({
    resolver: zodResolver(sourceLayout ? sourceContactSchema : contactSchema),
    defaultValues: { name: "", firstName: "", lastName: "", email: "", company: "", message: "" },
  })
  const { onError } = useFormErrorHandling(form)
  const [submit, { error }] = useMutation(CreateContactSubmissionDocument, { onError: () => null })
  const [status, setStatus] = useState<"idle" | "success">("idle")
  const [requestError, setRequestError] = useState<ErrorLike | null>(null)
  const analytics = useFormAnalytics("contact")

  const onSubmit = async (values: z.infer<typeof contactValuesSchema>) => {
    setStatus("idle")
    setRequestError(null)
    try {
      const result = await submit({
        variables: {
          input: {
            contactSubmissionInput: {
              name: sourceLayout ? `${values.firstName} ${values.lastName}` : values.name,
              email: values.email,
              company: values.company || undefined,
              message: values.message,
              source: "website",
              idempotencyKey: crypto.randomUUID(),
            },
          },
        },
      })

      if (result.error) {
        onError(result.error)
        return
      }

      setStatus("success")
      trackLead("contact")
      form.reset()
    } catch (error) {
      const error_ = mutationError(error)
      setRequestError(error_)
      onError(error_)
    }
  }

  const disabled = form.formState.isSubmitting

  return (
    <Form {...form}>
      <SubmissionPanel form="contact" onFocusCapture={analytics.onFocusCapture} status={status} />
      {status === "idle" && (
        <form
          onSubmit={form.handleSubmit(onSubmit, analytics.onInvalid)}
          onFocusCapture={analytics.onFocusCapture}
          className="max-w-2xl space-y-5"
          noValidate
        >
          <FormGeneralErrors control={form.control} />
          {error ? (
            <GraphqlError error={error} />
          ) : (
            requestError && <GraphqlError error={requestError} />
          )}
          {disabled && (
            <p role="status" className="sr-only">
              Sending your message.
            </p>
          )}
          {sourceLayout ? (
            <>
              <div className="grid gap-5 sm:grid-cols-2">
                <TextField
                  control={form.control}
                  name="firstName"
                  label="First Name"
                  required
                  disabled={disabled}
                />
                <TextField
                  control={form.control}
                  name="lastName"
                  label="Last Name"
                  required
                  disabled={disabled}
                />
              </div>
              <TextField
                control={form.control}
                name="email"
                label="Email Address"
                type="email"
                required
                disabled={disabled}
              />
              <TextField
                control={form.control}
                name="company"
                label="Company"
                disabled={disabled}
              />
              <MessageField
                control={form.control}
                name="message"
                label="Message"
                disabled={disabled}
                rows={3}
              />
            </>
          ) : (
            <>
              <div className="grid gap-5 sm:grid-cols-2">
                <TextField
                  control={form.control}
                  name="name"
                  label="Name"
                  required
                  disabled={disabled}
                />
                <TextField
                  control={form.control}
                  name="email"
                  label="Email"
                  type="email"
                  required
                  disabled={disabled}
                />
              </div>
              <TextField
                control={form.control}
                name="company"
                label="Company (optional)"
                disabled={disabled}
              />
              <MessageField
                control={form.control}
                name="message"
                label="How can we help?"
                disabled={disabled}
              />
            </>
          )}
          <Button type="submit" disabled={disabled}>
            {disabled
              ? "Sending…"
              : (submitLabel ?? (sourceLayout ? "Get In Touch" : "Send message"))}
          </Button>
        </form>
      )}
    </Form>
  )
}

export const NewsletterForm = ({ placement }: { placement: string }) => {
  const form = useForm<z.infer<typeof newsletterSchema>>({
    resolver: zodResolver(newsletterSchema),
    defaultValues: { email: "" },
  })
  const { onError } = useFormErrorHandling(form)
  const [submit, { error }] = useMutation(CreateNewsletterSubscriptionDocument, {
    onError: () => null,
  })
  const [status, setStatus] = useState<"idle" | "success">("idle")
  const [requestError, setRequestError] = useState<ErrorLike | null>(null)
  const analytics = useFormAnalytics("newsletter")

  const onSubmit = async (values: z.infer<typeof newsletterSchema>) => {
    setStatus("idle")
    setRequestError(null)
    try {
      const result = await submit({
        variables: {
          input: {
            newsletterSubscriptionInput: {
              email: values.email,
              source: placement,
              idempotencyKey: crypto.randomUUID(),
            },
          },
        },
      })

      if (result.error) {
        onError(result.error)
        return
      }

      setStatus("success")
      trackNewsletterSignup(placement)
      form.reset()
    } catch (error) {
      const error_ = mutationError(error)
      setRequestError(error_)
      onError(error_)
    }
  }

  const disabled = form.formState.isSubmitting

  return (
    <Form {...form}>
      <SubmissionPanel
        form="newsletter"
        onFocusCapture={analytics.onFocusCapture}
        status={status}
      />
      {status === "idle" && (
        <form
          onSubmit={form.handleSubmit(onSubmit, analytics.onInvalid)}
          onFocusCapture={analytics.onFocusCapture}
          className="w-full max-w-md space-y-4"
          noValidate
        >
          <FormGeneralErrors control={form.control} />
          {error ? (
            <GraphqlError error={error} />
          ) : (
            requestError && <GraphqlError error={requestError} />
          )}
          {disabled && (
            <p role="status" className="sr-only">
              Subscribing you to the newsletter.
            </p>
          )}
          <TextField
            control={form.control}
            name="email"
            label="Email"
            type="email"
            required
            disabled={disabled}
          />
          <Button type="submit" disabled={disabled}>
            {disabled ? "Subscribing…" : "Subscribe"}
          </Button>
        </form>
      )}
    </Form>
  )
}

export const FounderForm = () => {
  const form = useForm<z.infer<typeof founderSchema>>({
    resolver: zodResolver(founderSchema),
    defaultValues: { firstName: "", lastName: "", email: "" },
  })
  const { onError } = useFormErrorHandling(form)
  const [submit, { error }] = useMutation(CreateFounderSubmissionDocument, { onError: () => null })
  const [status, setStatus] = useState<"idle" | "success">("idle")
  const [courseEnabled, setCourseEnabled] = useState(false)
  const [requestError, setRequestError] = useState<ErrorLike | null>(null)
  const analytics = useFormAnalytics("founder")

  const onSubmit = async (values: z.infer<typeof founderSchema>) => {
    setStatus("idle")
    setCourseEnabled(false)
    setRequestError(null)
    try {
      const result = await submit({
        variables: {
          input: {
            founderSubmissionInput: {
              name: `${values.firstName} ${values.lastName}`,
              email: values.email,
              company: "",
              message: "",
              source: "website",
              idempotencyKey: crypto.randomUUID(),
            },
          },
        },
      })

      if (result.error) {
        onError(result.error)
        return
      }

      setStatus("success")
      setCourseEnabled(result.data?.founderSubmissionCreate.courseEnabled ?? false)
      trackLead("founder")
      form.reset()
    } catch (error) {
      const error_ = mutationError(error)
      setRequestError(error_)
      onError(error_)
    }
  }

  const disabled = form.formState.isSubmitting

  return (
    <Form {...form}>
      <SubmissionPanel
        form="founder"
        onFocusCapture={analytics.onFocusCapture}
        status={status}
        courseEnabled={courseEnabled}
      />
      {status === "idle" && (
        <form
          onSubmit={form.handleSubmit(onSubmit, analytics.onInvalid)}
          onFocusCapture={analytics.onFocusCapture}
          className="founder-signup-form"
          noValidate
        >
          <FormGeneralErrors control={form.control} />
          {error ? (
            <GraphqlError error={error} />
          ) : (
            requestError && <GraphqlError error={requestError} />
          )}
          {disabled && (
            <p role="status" className="sr-only">
              Subscribing to the email series.
            </p>
          )}
          <div className="founder-name-fields">
            <TextField
              control={form.control}
              name="firstName"
              label="First Name"
              required
              disabled={disabled}
            />
            <TextField
              control={form.control}
              name="lastName"
              label="Last Name"
              required
              disabled={disabled}
            />
          </div>
          <TextField
            control={form.control}
            name="email"
            label="Email Address"
            type="email"
            required
            disabled={disabled}
          />
          <Button className="service-cta founder-cta" type="submit" disabled={disabled}>
            {disabled ? "Subscribing…" : "Subscribe Today"}
          </Button>
        </form>
      )}
    </Form>
  )
}
